import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HotelIcon from '@mui/icons-material/Hotel';
import {
  createRoomRevenueKpiBasis,
  deleteRoomRevenueKpiBasis,
  getRevenueBuckets,
  getRoomRevenueKpiBasis,
  updateRoomRevenueKpiBasis,
} from './api';
import { canManageRoomRevenueKpiBasis, canViewRoomRevenueKpiBasis } from './utils/permissions';
import AdminDialogShell from '../../components/ui/AdminDialogShell';
import AsyncState from '../../components/ui/AsyncState';
import SectionCard from '../../components/ui/SectionCard';

function unwrapList(data) {
  return data?.results || (Array.isArray(data) ? data : []);
}

function formatDate(value) {
  if (!value) return 'N/D';
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function parseError(error) {
  const payload = error.response?.data;
  if (!payload) return 'Errore durante il salvataggio.';
  if (typeof payload.detail === 'string') return payload.detail;
  return Object.values(payload).flat().filter(Boolean).join(' ') || 'Errore durante il salvataggio.';
}

export default function RoomRevenueKPIBasisManager({ selectedPropertyId }) {
  const currentUser = useSelector((state) => state.user.user);
  const { enqueueSnackbar } = useSnackbar();
  const canEdit = canManageRoomRevenueKpiBasis(currentUser);
  const canView = canViewRoomRevenueKpiBasis(currentUser) || canEdit;

  const [bases, setBases] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBase, setEditingBase] = useState(null);
  const [formState, setFormState] = useState({ revenue_bucket_id: '', is_active: true });

  const activeBuckets = useMemo(() => buckets.filter((bucket) => bucket.is_active), [buckets]);
  const selectedBucketIds = useMemo(() => new Set(bases.map((item) => item.revenue_bucket?.id).filter(Boolean)), [bases]);
  const availableBuckets = useMemo(
    () => activeBuckets.filter((bucket) => !selectedBucketIds.has(bucket.id) || bucket.id === editingBase?.revenue_bucket?.id),
    [activeBuckets, editingBase, selectedBucketIds]
  );

  const fetchData = useCallback(async () => {
    if (!selectedPropertyId) {
      setBases([]);
      setBuckets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [basisRows, bucketRows] = await Promise.all([
        getRoomRevenueKpiBasis(selectedPropertyId),
        getRevenueBuckets(),
      ]);
      setBases(unwrapList(basisRows));
      setBuckets(unwrapList(bucketRows));
    } catch {
      setError('Impossibile caricare la base revenue KPI camere.');
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = () => {
    setEditingBase(null);
    setFormState({ revenue_bucket_id: availableBuckets[0]?.id || '', is_active: true });
    setDialogOpen(true);
  };

  const openEditDialog = (basis) => {
    setEditingBase(basis);
    setFormState({
      revenue_bucket_id: basis.revenue_bucket?.id || '',
      is_active: Boolean(basis.is_active),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingBase(null);
  };

  const handleSubmit = async () => {
    if (!formState.revenue_bucket_id) {
      enqueueSnackbar('Seleziona un bucket di revenue.', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        property_id: selectedPropertyId,
        revenue_bucket_id: formState.revenue_bucket_id,
        is_active: formState.is_active,
      };

      if (editingBase) {
        await updateRoomRevenueKpiBasis(editingBase.id, payload);
      } else {
        await createRoomRevenueKpiBasis(payload);
      }

      enqueueSnackbar('Base revenue KPI camere salvata.', { variant: 'success' });
      setDialogOpen(false);
      setEditingBase(null);
      await fetchData();
    } catch (requestError) {
      enqueueSnackbar(parseError(requestError), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (basis) => {
    if (!window.confirm('Rimuovere questa base revenue KPI camere?')) return;

    try {
      await deleteRoomRevenueKpiBasis(basis.id);
      enqueueSnackbar('Mapping rimosso.', { variant: 'info' });
      await fetchData();
    } catch {
      enqueueSnackbar('Errore durante la rimozione del mapping.', { variant: 'error' });
    }
  };

  if (!selectedPropertyId) {
    return <Alert severity="info">Seleziona una property per gestire la base revenue KPI camere.</Alert>;
  }

  if (!canView) {
    return <Alert severity="warning">Non hai accesso a questa sezione.</Alert>;
  }

  if (loading) {
    return <AsyncState state="loading" message="Caricamento mapping revenue KPI camere" fullWidth />;
  }

  return (
    <Stack spacing={3}>
      <SectionCard title="Room Revenue KPI" description="Configura i bucket che alimentano ADR e RevPAR per questa property." sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
              <HotelIcon color="primary" />
              <Typography variant="h5" fontWeight={800}>Room Revenue KPI</Typography>
            </Stack>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} disabled={!canEdit || availableBuckets.length === 0}>
            Nuovo mapping
          </Button>
        </Stack>
        {!canEdit ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Il tuo profilo può visualizzare questa sezione ma non modificarla.
          </Alert>
        ) : null}
        {availableBuckets.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Non ci sono bucket attivi disponibili da assegnare.
          </Alert>
        ) : null}
      </SectionCard>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {bases.length === 0 ? (
        <AsyncState
          state="empty"
          title="Nessun mapping configurato"
          message="Aggiungi il primo bucket per iniziare a sincronizzare la base revenue camere."
          action={<Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} disabled={!canEdit || availableBuckets.length === 0}>Aggiungi mapping</Button>}
          fullWidth
        />
      ) : (
        <Stack spacing={1.5}>
          {bases.map((basis) => (
            <Paper key={basis.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                <Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip label={basis.revenue_bucket?.code || 'N/A'} color="primary" />
                    <Chip label={basis.revenue_bucket?.name || 'Bucket'} variant="outlined" />
                    <Chip label={basis.is_active ? 'Attivo' : 'Disattivo'} color={basis.is_active ? 'success' : 'default'} />
                  </Stack>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {basis.revenue_bucket?.name || basis.revenue_bucket?.code || 'Bucket senza nome'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Periodo: {basis.revenue_bucket?.period || 'N/D'} · Aggiornato: {formatDate(basis.updated_at)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(basis)} disabled={!canEdit}>
                    Modifica
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(basis)} disabled={!canEdit}>
                    Elimina
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <AdminDialogShell
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        title={editingBase ? 'Modifica mapping' : 'Nuovo mapping'}
        actions={<><Button onClick={closeDialog} disabled={saving}>Annulla</Button><Button onClick={handleSubmit} variant="contained" disabled={saving || !canEdit}>{saving ? 'Salvataggio...' : 'Salva'}</Button></>}
      >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="room-revenue-kpi-bucket-label">Bucket di revenue</InputLabel>
              <Select
                labelId="room-revenue-kpi-bucket-label"
                label="Bucket di revenue"
                value={formState.revenue_bucket_id}
                onChange={(event) => setFormState((current) => ({ ...current, revenue_bucket_id: event.target.value }))}
              >
                {availableBuckets.map((bucket) => (
                  <MenuItem key={bucket.id} value={bucket.id}>
                    {bucket.code} - {bucket.name || bucket.code} {bucket.period ? `(${bucket.period})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={formState.is_active}
                onChange={(event) => setFormState((current) => ({ ...current, is_active: event.target.checked }))}
              />
              <Typography variant="body2">Mapping attivo</Typography>
            </Stack>
          </Stack>
      </AdminDialogShell>
    </Stack>
  );
}
